import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShieldAlt, 
  faKey, 
  faUsers, 
  faCog,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import ActivationCodeManager from '../components/admin/ActivationCodeManager';

/**
 * Pagina di amministrazione del sistema
 */
const AdminPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('activationCodes');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Verifica se l'utente è un amministratore
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!auth.currentUser) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        
        if (!userDoc.exists()) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        setIsAdmin(userData.role === 'admin');
        setLoading(false);
      } catch (error) {
        console.error('Errore nella verifica dello stato di admin:', error);
        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Se l'utente non è autenticato o non è un amministratore, reindirizza alla dashboard
  if (!loading && (!auth.currentUser || !isAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container className="py-4">
      <h1 className="mb-4">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
        {t('admin.title')}
      </h1>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3 text-muted">{t('common.loading')}</p>
        </div>
      ) : (
        <Row>
          <Col md={3} lg={2}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-primary text-white">
                <FontAwesomeIcon icon={faCog} className="me-2" />
                {t('common.menu')}
              </Card.Header>
              <Card.Body className="p-0">
                <Nav className="flex-column">
                  <Nav.Link 
                    active={activeTab === 'activationCodes'} 
                    onClick={() => setActiveTab('activationCodes')}
                    className="d-flex align-items-center ps-3 py-3 border-bottom"
                  >
                    <FontAwesomeIcon icon={faKey} className="me-2" />
                    {t('admin.activationCodes.title')}
                  </Nav.Link>
                  <Nav.Link 
                    active={activeTab === 'users'} 
                    className="d-flex align-items-center ps-3 py-3 border-bottom"
                    disabled
                  >
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    {t('admin.users.title')}
                  </Nav.Link>
                  <Nav.Link 
                    active={activeTab === 'security'} 
                    className="d-flex align-items-center ps-3 py-3"
                    disabled
                  >
                    <FontAwesomeIcon icon={faLock} className="me-2" />
                    {t('admin.security.title')}
                  </Nav.Link>
                </Nav>
              </Card.Body>
            </Card>
          </Col>
          <Col md={9} lg={10}>
            {activeTab === 'activationCodes' && <ActivationCodeManager />}
            {activeTab === 'users' && (
              <Alert variant="info">
                La gestione degli utenti sarà disponibile in una futura versione.
              </Alert>
            )}
            {activeTab === 'security' && (
              <Alert variant="info">
                Le impostazioni di sicurezza saranno disponibili in una futura versione.
              </Alert>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default AdminPage; 